import React from 'react'

export enum JsonLDType {
  Article = 'Article',
  BreadcrumbList = 'BreadcrumbList',
  ContactPoint = 'ContactPoint',
  ImageObject = 'ImageObject',
  ListItem = 'ListItem',
  Org = 'Organization',
  Person = 'Person',
  PostalAddress = 'PostalAddress',
  WebPage = 'WebPage',
}

export interface JsonLDArticle {
  type: JsonLDType.Article
  schema: {
    author?: JsonLDPerson | JsonLDOrganization
    datePublished: string
    dateModified?: string
    description: string | null
    headline: string
    image?: string[] | string
    mainEntityOfPage: JsonWebPage
    publisher?: JsonLDOrganization
    url: string
  }
}

export interface JsonLDBreadcrumbList {
  type: JsonLDType.BreadcrumbList
  schema: {
    itemListElement: JsonLDListItem[]
  }
}

export interface JsonLDContactPoint {
  type: JsonLDType.ContactPoint
  schema: {
    contactType: string
    email: string
  }
}

export interface JsonLDImageObject {
  type: JsonLDType.ImageObject
  schema: {
    url: string
  }
}

export interface JsonLDListItem {
  type: JsonLDType.ListItem
  schema: {
    position: number
    name: string
    item: string
  }
}

export interface JsonLDOrganization {
  type: JsonLDType.Org
  schema: {
    address: JsonLDPostalAddress
    contactPoint: JsonLDContactPoint
    areaServed?: string
    description: string
    legalName: string
    logo: JsonLDImageObject
    name: string
    sameAs: string[]
    url: string
  }
}

export interface JsonLDPerson {
  type: JsonLDType.Person
  schema: {
    name: string
  }
}

export interface JsonLDPostalAddress {
  type: JsonLDType.PostalAddress
  schema: {
    streetAddress: string
    addressLocality: string
    addressRegion: string
    postalCode: string
    addressCountry: string
  }
}

export interface JsonWebPage {
  type: JsonLDType.WebPage
  schema: {
    id: string
  }
}

type JsonLDProps = JsonLDArticle | JsonLDBreadcrumbList | JsonLDPerson | JsonLDOrganization

const atPrefixed = ['id', 'type']

const transformSchema = (schema: any): any =>
  Object.entries(schema).reduce((entries, entry) => {
    if (atPrefixed.includes(entry[0])) {
      return {
        ...entries,
        [`@${entry[0]}`]: entry[1],
      }
    }

    if (entry[0] === 'schema') {
      return {
        ...entries,
        ...transformSchema(entry[1]),
      }
    }

    if (Array.isArray(entry[1])) {
      return {
        ...entries,
        [entry[0]]: entry[1].map((arrayItem) =>
          typeof arrayItem === 'object' ? transformSchema(arrayItem) : arrayItem,
        ),
      }
    }

    if (typeof entry[1] === 'object' && entry[1] !== null) {
      return {
        ...entries,
        [entry[0]]: transformSchema(entry[1]),
      }
    }

    return {
      ...entries,
      [entry[0]]: entry[1],
    }
  }, {})

const JsonLD: React.FC<JsonLDProps> = ({ schema, type }) => {
  return (
    <script
      type="application/ld+json"
      data-testid={`JsonLD/${type}`}
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({ '@context': 'https://schema.org', ...transformSchema({ schema, type }) }, null, 2),
      }}
    />
  )
}

export default JsonLD
